import { CaptionColorPicker, CaptionStyleCarousel } from '@/components/forms/CaptionStyleCarousel'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { Segmented } from '@/components/ui/Segmented'
import { Toggle } from '@/components/ui/Toggle'
import {
  CTA_ASK_LABELS,
  CTA_ASKS,
  KARAOKE_STYLE,
  NO_PRESET_VALUE,
  NO_SUBTITLE_FACTORIES,
  SUBTITLE_POSITIONS,
  usesActiveColor,
  type ScheduleValues,
} from './model'
import { Field, Section } from './parts'
import styles from './ChannelEditDialog.module.scss'

export interface StyleTabProps {
  values: ScheduleValues
  set: <K extends keyof ScheduleValues>(key: K, value: ScheduleValues[K]) => void
  before: (key: keyof ScheduleValues) => string | undefined
  factoryLabel: string
}

/**
 * Aba "Estilo de vídeo": a legenda da geração agendada deste canal ou pool e o
 * pedido de like/comentário/inscrição (``video_style.engagement``).
 *
 * Grava em ``video_style`` (migration 0093), que vence o preset e o padrão das
 * Configurações. Vazio segue os dois, como antes da aba. Escolher o karaokê abre
 * a personalização dele (a cor da palavra sendo lida).
 */
export function StyleTab({ values, set, before, factoryLabel }: StyleTabProps) {
  const style = values.subtitle_style
  const noSubtitles = NO_SUBTITLE_FACTORIES.has(values.factory)
  const hasPreset = values.preset_id !== NO_PRESET_VALUE && values.preset_id !== ''
  const asks = values.cta_asks

  return (
    <div className={styles.tabPanel}>
      {noSubtitles ? (
        <Callout title={`${factoryLabel} não queima legenda`}>
          Os vídeos desta factory saem sem legenda, então o estilo escolhido aqui não muda nada neles.
        </Callout>
      ) : null}
      <Section title="Legenda" meta={style ? 'deste canal' : 'padrão'}>
        <Field
          groupId="ch-subtitle-style"
          label="Estilo de legenda"
          help="Vale para a geração automática e o Gerar agora, por cima do preset. Sem escolha, a legenda segue o preset ou o padrão das Configurações."
          meta={style ? undefined : hasPreset ? 'segue o preset' : 'segue o padrão'}
          before={before('subtitle_style')}
          hint={
            style ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  set('subtitle_style', '')
                }}
              >
                Voltar ao padrão
              </Button>
            ) : (
              'Escolha um estilo para fixar a legenda deste canal.'
            )
          }
        >
          <CaptionStyleCarousel
            value={style}
            onValueChange={(v) => {
              set('subtitle_style', v)
            }}
            activeColor={values.subtitle_active_color}
          />
        </Field>
        <Field
          groupId="ch-subtitle-position"
          label="Posição da legenda"
          help="No meio ou embaixo do vídeo, por cima do que a factory escolhe (o futebol e o APOD põem embaixo; o cortes, no meio)."
          before={before('subtitle_position')}
        >
          <Segmented
            aria-label="Posição da legenda"
            options={SUBTITLE_POSITIONS}
            value={values.subtitle_position}
            onValueChange={(v) => {
              set('subtitle_position', v)
            }}
            size="sm"
          />
        </Field>
      </Section>

      <Section title="Pedir engajamento" meta={asks.length ? `${asks.length} de ${CTA_ASKS.length}` : 'desligado'}>
        <Field
          groupId="ch-cta-asks"
          label="O que pedir"
          help="Um adesivo animado entra por cima do vídeo pronto, em qualquer factory: comentário cedo, like no meio e inscrição perto do fim. Nunca nos 3 primeiros segundos, nem no último. O comentário usa a pergunta do roteiro quando a factory gera uma."
          before={before('cta_asks')}
          hint="Cada vídeo sorteia a sua combinação entre os ligados."
        >
          <div className={styles.ctaAsks} role="group" aria-labelledby="ch-cta-asks-label">
            {CTA_ASKS.map((ask) => (
              <Toggle
                key={ask}
                id={`ch-cta-${ask}`}
                label={CTA_ASK_LABELS[ask]}
                checked={asks.includes(ask)}
                onCheckedChange={(on) => {
                  set(
                    'cta_asks',
                    CTA_ASKS.filter((a) => (a === ask ? on : asks.includes(a))),
                  )
                }}
              />
            ))}
          </div>
        </Field>
        {asks.length ? (
          <div className={styles.grid2}>
            <Field
              label="Pedidos por vídeo"
              help="Quantos dos ligados cada vídeo mostra. Com Like e Inscreva-se ligados e 1 por vídeo, metade sai com um e metade com o outro."
              before={before('cta_per_video')}
            >
              <Segmented
                aria-label="Pedidos por vídeo"
                options={[1, 2, 3].map((n) => ({ value: String(n), label: String(n) }))}
                value={String(values.cta_per_video)}
                onValueChange={(v) => {
                  set('cta_per_video', Number(v))
                }}
              />
            </Field>
            <Field
              label="Vídeos com pedido"
              help="Os vídeos que ficam sem pedido são o grupo de controle: é com eles que a correlação do Analytics compara a taxa de like e de comentário. Com 100%, não há com o que comparar."
              before={before('cta_share_pct')}
            >
              <Segmented
                aria-label="Vídeos com pedido"
                options={[
                  { value: '50', label: '50%' },
                  { value: '80', label: '80%' },
                  { value: '100', label: '100%' },
                ]}
                value={String(values.cta_share_pct)}
                onValueChange={(v) => {
                  set('cta_share_pct', Number(v))
                }}
              />
            </Field>
          </div>
        ) : null}
      </Section>

      {usesActiveColor(style) ? (
        <Section title={style === KARAOKE_STYLE ? 'Personalizar o karaokê' : 'Se o sorteio cair no karaokê'}>
          <Field
            groupId="ch-subtitle-color"
            label="Cor da palavra sendo lida"
            help="No karaokê, a frase fica numa caixa escura: o que ainda não foi lido em cinza, a palavra sendo lida nesta cor e o que já foi lido em branco."
            before={before('subtitle_active_color')}
          >
            <CaptionColorPicker
              value={values.subtitle_active_color}
              onValueChange={(c) => {
                set('subtitle_active_color', c)
              }}
            />
          </Field>
        </Section>
      ) : null}
    </div>
  )
}
